import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveBrandGamificationItems } from '@/store/slices/casino-instance';
import { selectNShowcase } from '@/store/slices/userPreferences';
import { fetchInvoices, buildPendingFeatureInvoiceMap } from '@/store/slices/wallet';
import type { GamificationItemWithTiers } from '@/store/slices/configurator-config';
import type { GamificationTier } from '@/components/modules/spark/configurator/types';
import type { GamificationItem as BrandGamificationItem } from '@/components/modules/brands-selector';

import { PageShell, InsightsPanel, SectionTitle, FeatureCard, EmptyState, Icon } from '../../components';
import { ROUTE } from '../../routes';
import { ANIM, COLORS, gaugeTone, type FeatureTone, type GaugeStatusLabel } from '../../theme/tokens';
import { useMountAnimation } from '../../hooks/useMountAnimation';
import { useProductScore } from '../../hooks/useProductScore';
import { publishLumioInactiveFeatures } from '../../lib/lumioSignals';
import {
  artBundleText,
  axisMaxTotals,
  DEFAULT_LANG,
  DEFAULT_REGION,
  fallbackMarketLevel,
  type FeatureScores,
  isActiveItem,
  marketLevelForRegion,
  normalizeScores,
  scaleDeltas,
  scoreDeltasToTop,
  tierShortLabelFor,
  visibleTiers,
} from '../../utils/catalog';
import type { InsightMetric, InsightsSummary, ProductFeature, ProductFeatureBenefit } from '../../mock-data/types';
import {
  Section,
  Container,
  FeatureList,
  Drawer,
  DrawerInner,
  DrawerList,
  SeeMoreToggle,
  CalibrationNote,
  CalibrationNoteIcon,
  CalibrationNoteText,
} from './product.styles';

export type ProductProps = {
  styleText?: string;
  className?: string;
};

/*
 * Both feature sections derive from the catalog in `configuratorConfig.gamificationItems`
 * (items + tier ladders from `/configurator`) joined with the active brand's purchases
 * (`selectActiveBrandGamificationItems`). API data is expected to be complete — no display
 * fallbacks. A purchase sitting on its item's "off" tier (the tier with priority 0) means
 * the feature is NOT enabled; the other tiers mark it enabled, and the "Level x/y" label
 * is the tier's position among those enabled tiers (off excluded), so an off/plus/pro/ultra
 * ladder renders pro as "Level 2/3".
 * - Enabled Features: purchases on a non-off tier (a purchase on a platform-disabled item still
 *   renders, the owner is paying for it, but it contributes nothing to the score).
 * - Missing Features: ACTIVE catalog items not enabled (no purchase, or purchase on the off
 *   tier). Platform-disabled items (`is_active: false`) are not offered: they cannot be
 *   activated and sit outside the scoring budget. First three always visible; the rest expand
 *   via the "See more features" drawer. The section hides entirely when nothing is missing.
 * A card's ladder (level / max / next tier) follows the catalog mask (`visibleTiers`, shared with
 * the product-detail page): the item's active rungs plus the tier this brand owns even when it is
 * retired, so a rung nobody can buy is never counted or promised. The unlock deltas come from the
 * SCORING ladder (active, scored tiers), in points of each axis's 100 budget, so they are never
 * negative. Data no API provides yet is tracked in ./missing-data.md.
 */
/** The three competitiveness benefits shown on every card, in display order. Labels are
 *  static copy (rendered through <Text>); values are per-feature score deltas. */
const BENEFIT_LABELS: { id: string; label: string; key: keyof FeatureScores }[] = [
  { id: 'market-score', label: 'Market Score', key: 'market' },
  { id: 'profitability', label: 'Profitability', key: 'profitability' },
  { id: 'stickiness', label: 'Stickiness', key: 'stickiness' },
];

/** Benefit rows from a feature's normalized score deltas (points of each axis's 100 budget, non-
 *  negative by construction); rendered as "+N". The deltas are scaled by the launch-calibration
 *  multiplier (`scale`, 1 per axis once calibration ends) so that while a fresh website's score is
 *  lifted, the cards' remaining deltas shrink in step and the panel score + every card still sum
 *  to the full budget (100 per axis). */
const benefitsFromScores = (deltas: FeatureScores, scale: FeatureScores): ProductFeatureBenefit[] => {
  const scaled = scaleDeltas(deltas, scale);
  return BENEFIT_LABELS.map(({ id, label, key }) => ({
    id,
    label,
    value: `+${scaled[key]}`,
  }));
};

/** True when at least one axis has something left to unlock. */
const hasHeadroom = (deltas: FeatureScores): boolean =>
  deltas.market > 0 || deltas.profitability > 0 || deltas.stickiness > 0;

/** Map a purchased gamification item onto the card's data shape; null = not enabled (off tier) or catalog not loaded yet.
 *  `maxTotals` is the catalog's per-axis scoring budget (`axisMaxTotals`), the denominator the raw deltas are normalized against. */
const featureFromApiItem = (
  item: BrandGamificationItem,
  catalog: GamificationItemWithTiers[],
  lang: string,
  regionCode: string,
  scale: FeatureScores,
  maxTotals: FeatureScores,
  pendingTierId: number | undefined,
): ProductFeature | null => {
  const catalogItem = catalog.find((entry) => entry.id === item.item_id);
  if (!catalogItem) {
    return null;
  }

  // The card's ladder follows the catalog mask, exactly like the detail page: the item's offered
  // rungs plus the tier this brand owns and the tier an unpaid invoice targets, even when retired
  // (`item.tier_id` is typed `string | number` but the selector already coerces it; `Number()`
  // satisfies the type). A purchase not in it sits on the off tier (or an unknown one): not enabled.
  const ladder = visibleTiers(catalogItem, Number(item.tier_id), pendingTierId);
  const tierIndex = ladder.findIndex((tier) => tier.id === item.tier_id);
  if (tierIndex < 0) {
    return null;
  }

  const maxLevel = ladder.length;
  const level = tierIndex + 1;
  const nextTier: GamificationTier | undefined = ladder[tierIndex + 1];
  // Raw unlock deltas against the item's top SCORING tier (never negative). All zero with a rung
  // still above (an active but unscored rung, or a disabled item) means nothing quantifiable to
  // gain: the card keeps its upgrade line but shows no "+0" figures.
  const rawDeltas = scoreDeltasToTop(catalogItem, ladder[tierIndex]);

  return {
    // The catalog item id — detail navigation targets the item, enabled or not.
    id: String(item.item_id),
    image: catalogItem.art_bundle?.[0]?.logo ?? '',
    name: artBundleText(catalogItem.art_bundle?.[0], lang) ?? catalogItem.name,
    level,
    maxLevel,
    // The SHORT tier token everywhere on the card ("PLUS", not "META JACKPOT - PLUS"): the card
    // already names the feature above, so the catalog's marketing prefix only repeats it — and it
    // wraps the status line onto two lines and outgrows the bar's pill.
    tierLabel: tierShortLabelFor(ladder[tierIndex], catalogItem, lang),
    nextTierLabel: nextTier ? tierShortLabelFor(nextTier, catalogItem, lang) : undefined,
    bar: {
      value: Math.round((level / maxLevel) * 100),
      // Per-region market benchmark from the art bundle (already 0-100); falls back to
      // the middle-of-ladder convention until the data ships (see ./missing-data.md).
      market: marketLevelForRegion(catalogItem, regionCode) ?? fallbackMarketLevel(maxLevel),
    },
    // Benefit deltas = top SCORING tier minus the owned tier's credit, in points of the axis budget (see ./missing-data.md).
    benefits: hasHeadroom(rawDeltas) ? benefitsFromScores(normalizeScores(rawDeltas, maxTotals), scale) : [],
  };
};

/** Map a not-enabled catalog item onto an inactive card (`maxTotals`, `pendingTierId`: see {@link featureFromApiItem}). */
const featureFromCatalogItem = (
  catalogItem: GamificationItemWithTiers,
  lang: string,
  regionCode: string,
  scale: FeatureScores,
  maxTotals: FeatureScores,
  pendingTierId: number | undefined,
): ProductFeature => {
  // Offered rungs plus the tier an unpaid invoice targets (nothing owned here).
  const maxLevel = visibleTiers(catalogItem, undefined, pendingTierId).length;
  // Full top scoring value (current = 0); an item with no scored rung has nothing quantifiable.
  const rawDeltas = scoreDeltasToTop(catalogItem, undefined);

  return {
    id: String(catalogItem.id),
    image: catalogItem.art_bundle?.[0]?.logo ?? '',
    name: artBundleText(catalogItem.art_bundle?.[0], lang) ?? catalogItem.name,
    level: null,
    maxLevel,
    bar: {
      value: 0,
      // Per-region market benchmark from the art bundle (already 0-100); falls back to
      // the middle-of-ladder convention until the data ships (see ./missing-data.md).
      market: marketLevelForRegion(catalogItem, regionCode) ?? fallbackMarketLevel(maxLevel),
    },
    // Inactive feature: benefit deltas are the full top SCORING tier value, normalized.
    benefits: hasHeadroom(rawDeltas) ? benefitsFromScores(normalizeScores(rawDeltas, maxTotals), scale) : [],
  };
};

/** How many missing-feature cards are visible before the "See more features" drawer. */
const MISSING_VISIBLE_COUNT = 3;

type FeatureView = {
  statusText: string;
  statusPlaceholders?: Record<string, string | number>;
  tone: FeatureTone;
  /** Tier pill on the comparison bar: the tier the feature runs on today, or "Off" when it is not
   *  enabled — the bar states where the owner STANDS, never what activating would unlock (that is
   *  the unlock divider + benefit deltas' job). Undefined only if the catalog yields no tier name
   *  for an enabled feature, in which case the bar renders without a pill. */
  tierLabel?: string;
  unlockText: string;
  unlockPlaceholders?: Record<string, string | number>;
  ctaLabel: string;
  /** Top-tier feature: every benefit delta is +0, so the card shows a single
   *  "Fully enabled" divider and `renderCard` drops the (all-zero) figures. */
  fullyEnabled?: boolean;
};

/** Card copy + tone derived from the feature's level state (inactive / leveling / maxed).
 *  The copy is translated reactively (`t` is passed in from the rendering component). The
 *  tier name is API/catalog data, so the "Level x/y" templates carry the static fallback. */
const viewFor = (feature: ProductFeature, t: TFunction): FeatureView => {
  if (feature.level === null) {
    return {
      statusText: t('Inactive'),
      tone: 'inactive',
      tierLabel: t('Off'),
      unlockText: t('Activate {{name}} to unlock'),
      unlockPlaceholders: { name: feature.name },
      ctaLabel: t('Activate'),
    };
  }
  // Active features show the tier name (e.g. "Pro") under the feature name; the
  // "Level x/y" form is only a fallback when the tier has no display label.
  const statusText = feature.tierLabel ?? t('Level {{level}}/{{max}}');
  const statusPlaceholders = feature.tierLabel ? undefined : { level: feature.level, max: feature.maxLevel };
  if (feature.level >= feature.maxLevel) {
    // Top of the purchasable ladder reached: there is nothing left to unlock, so every benefit
    // delta is +0. Three "+0" figures just read as noise, collapse the block to a single
    // "Fully enabled" divider and let renderCard drop the figures (`fullyEnabled`).
    return {
      statusText,
      statusPlaceholders,
      tone: 'maxed',
      tierLabel: feature.tierLabel,
      unlockText: t('Fully enabled'),
      ctaLabel: t('See details'),
      fullyEnabled: true,
    };
  }
  return {
    statusText,
    statusPlaceholders,
    tone: 'leveling',
    tierLabel: feature.tierLabel,
    unlockText: feature.nextTierLabel ? t('Upgrade to {{tier}} to unlock') : t('Upgrade to Level {{level}} to unlock'),
    unlockPlaceholders: feature.nextTierLabel ? { tier: feature.nextTierLabel } : { level: feature.level + 1 },
    ctaLabel: t('See details'),
  };
};

/** Card view for a feature with an unpaid invoice: the owner started activating / upgrading it
 *  but never finished paying, so we replace the Activate CTA with a Finish-payment one and mark
 *  the status "Payment pending" in the in-progress (amber) tone. The unlock benefits stay as-is
 *  (they still describe what completing the payment unlocks). */
const pendingViewFor = (feature: ProductFeature, t: TFunction): FeatureView => ({
  statusText: t('Payment pending'),
  tone: 'leveling',
  tierLabel: feature.level === null ? t('Off') : feature.tierLabel,
  unlockText: feature.level === null ? t('Finish payment to activate {{name}}') : t('Finish payment to upgrade {{name}}'),
  unlockPlaceholders: { name: feature.name },
  ctaLabel: t('Finish payment'),
});

/**
 * Product root page: the website-competitiveness insights panel (metric-gauges
 * variant) followed by the missing- and enabled-feature card lists. Center
 * content only — the layout owns the title, consistent with the other roots.
 */
const Product = ({ styleText = '', className = '' }: ProductProps): React.ReactElement => {
  const animate = useMountAnimation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  // The three competitiveness benefit labels, translated reactively. The ids/keys
  // stay stable (they index the score totals); only the display label is localized.
  const benefitLabels: Record<string, string> = {
    'market-score': t('Market Score'),
    profitability: t('Profitability'),
    stickiness: t('Stickiness'),
  };
  // Score-band label (Poor / Sustainable / Optimal), translated at the render site.
  // `useProductScore` returns the English band; this is the shared gauge vocabulary.
  const statusLabels: Record<GaugeStatusLabel, string> = {
    Poor: t('Poor'),
    Sustainable: t('Sustainable'),
    Optimal: t('Optimal'),
  };
  // The headline verdict tracks the score band, so it never contradicts the gauge
  // (it was previously the static "competitive" line, which read wrong at low scores).
  const headlines: Record<GaugeStatusLabel, string> = {
    Poor: t('Your website needs attention'),
    Sustainable: t('Your website is competitive'),
    Optimal: t('Your website is leading the market'),
  };

  // The competitiveness insights panel is computed from the same configurator catalog +
  // brand purchases as the feature cards below, via `useProductScore` (the single source of
  // truth it shares with the Home dashboard's Product gauge). Its gauges draw in once the
  // catalog resolves (`insightsRevealed`); `useProductScore` owns the catalog fetch.
  const { totals, score, status, calibrating, deltaScale, loading: insightsLoading } = useProductScore();
  // The Newton showcase shows no scores: no insights panel, no calibration note and no score deltas on the cards.
  const newtonShowcase = useAppSelector(selectNShowcase);
  const [insightsRevealed, setInsightsRevealed] = React.useState(false);
  React.useEffect(() => {
    if (insightsLoading) {
      setInsightsRevealed(false);
      return;
    }
    const id = requestAnimationFrame(() => setInsightsRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [insightsLoading]);

  // Headline summary + the three metric sub-gauges, derived from the score totals. The metric
  // titles reuse the cards' benefit labels (`BENEFIT_LABELS`) for one set of copy; each gauge's
  // tone/band follows its own value, and ring progress is clamped to the 0-100 gauge range.
  const summary: InsightsSummary = {
    title: headlines[status],
    score,
    status: statusLabels[status],
    tone: gaugeTone(score),
    progress: Math.min(100, Math.max(0, score)),
  };
  const metrics: InsightMetric[] = BENEFIT_LABELS.map(({ id, key }) => ({
    id,
    title: benefitLabels[id] ?? id,
    score: totals[key],
    tone: gaugeTone(totals[key]),
    progress: Math.min(100, Math.max(0, totals[key])),
  }));

  const enabledItems = useAppSelector(selectActiveBrandGamificationItems);
  const catalogItems = useAppSelector((state) => state.configuratorConfig.gamificationItems);

  // Owner invoices back the "Finish payment" state: a feature the owner began activating /
  // upgrading but never paid for keeps an open invoice. Fetch them so a card can offer to
  // finish the payment instead of activating from scratch. Owner-scoped, so we match them to
  // the active brand below.
  React.useEffect(() => {
    dispatch(fetchInvoices());
  }, [dispatch]);
  const invoices = useAppSelector((state) => state.ownerWallet.invoices);
  // The active brand's id scopes the invoices to this brand (reads `displayBrand`, like the
  // items, so it lags a brand switch in lockstep). Keyed by catalog item id → the pending
  // invoice for that feature (if any).
  const activeBrandId = useAppSelector((state) => state.casinoInstance.displayBrand?.id);
  const pendingInvoicesByItem = React.useMemo(
    () => buildPendingFeatureInvoiceMap(invoices, activeBrandId),
    [invoices, activeBrandId],
  );

  const lang = i18n.language || DEFAULT_LANG;
  // The operating region the active brand is deployed on drives the per-region market
  // benchmark; read from `displayBrand` (like the items) so it lags through a brand
  // switch. Falls back to the default region until /casino-instance/list emits it.
  const regionCode = useAppSelector(
    (state) => state.casinoInstance.displayBrand?.operating_region ?? DEFAULT_REGION,
  );

  const [missingExpanded, setMissingExpanded] = React.useState(false);

  const { enabledFeatures, missingFeatures, totalCatalogCount } = React.useMemo(() => {
    const enabled: ProductFeature[] = [];
    const enabledCatalogIds = new Set<number>();
    // The per-axis scoring budget every card's deltas are normalized against (same denominator
    // as the insights gauges, so the panel score + all remaining deltas sum to 100 per axis).
    const maxTotals = axisMaxTotals(catalogItems);
    // The tier an unpaid invoice would activate for an item, so a card's ladder keeps that rung even
    // when it is retired (the detail page does the same). Invoice tier ids are numeric already.
    const pendingTierFor = (catalogId: number): number | undefined => {
      const tierId = pendingInvoicesByItem.get(catalogId)?.invoice_data?.new_item?.tier_id;
      return tierId === undefined ? undefined : Number(tierId);
    };

    enabledItems.forEach((item) => {
      const feature = featureFromApiItem(item, catalogItems, lang, regionCode, deltaScale, maxTotals, pendingTierFor(item.item_id));
      if (feature !== null) {
        enabled.push(feature);
        enabledCatalogIds.add(item.item_id);
      }
    });

    // Only OFFERED catalog items can be missing: a platform-disabled one cannot be activated, and
    // neither can an active item whose every rung is retired (no dead "Activate" card for it).
    const offeredCatalog = catalogItems.filter(
      (catalogItem) => isActiveItem(catalogItem) && visibleTiers(catalogItem, undefined, pendingTierFor(catalogItem.id)).length > 0,
    );
    const missing = offeredCatalog
      .filter((catalogItem) => !enabledCatalogIds.has(catalogItem.id))
      .map((catalogItem) =>
        featureFromCatalogItem(catalogItem, lang, regionCode, deltaScale, maxTotals, pendingTierFor(catalogItem.id)),
      );

    // The "x of y" total: the offered catalog PLUS any masked-out item the brand still holds from an
    // earlier purchase (it renders in the Enabled list, so it must count in the total or the section
    // could read "7 of 6").
    const totalCatalogCount = new Set<number>([...offeredCatalog.map((catalogItem) => catalogItem.id), ...enabledCatalogIds]).size;

    return { enabledFeatures: enabled, missingFeatures: missing, totalCatalogCount };
  }, [enabledItems, catalogItems, pendingInvoicesByItem, lang, regionCode, deltaScale]);

  // Tell Lumio which inactive features this page offers and the Market Score each card
  // promises, as canonical values (the rendered copy is translated, so it cannot be read back).
  React.useEffect(() => {
    publishLumioInactiveFeatures(
      // A card with nothing quantifiable to gain carries no benefit rows, so it has no uplift to report.
      missingFeatures.flatMap((feature) => {
        const market = feature.benefits.find((benefit) => benefit.id === 'market-score');
        return market ? [{ key: feature.id, label: feature.name, market_uplift: Math.round(Number(market.value)) }] : [];
      }),
    );
    return () => publishLumioInactiveFeatures([]);
  }, [missingFeatures]);

  const onToggleMissing = React.useCallback(() => {
    setMissingExpanded((expanded) => !expanded);
  }, []);

  const onFeatureCta = React.useCallback(
    (id: string) => {
      navigate(ROUTE.productDetail(id));
    },
    [navigate],
  );

  const renderCard = (feature: ProductFeature, index: number): React.ReactElement => {
    // A feature with an open (unpaid) invoice shows a "Finish payment" card instead of the
    // usual Activate / See-details one — the payment, not the tier choice, is what's left.
    const hasPendingInvoice = pendingInvoicesByItem.has(Number(feature.id));
    const view = hasPendingInvoice ? pendingViewFor(feature, t) : viewFor(feature, t);
    // The benefit labels share the closed benefit vocabulary above; translate
    // them reactively at the render site (the delta value is data, kept as-is).
    // A fully-enabled (top-tier) feature has only +0 deltas, so we hand the card
    // no benefits — it renders the "Fully enabled" divider with no figures.
    const benefits = view.fullyEnabled
      ? []
      : feature.benefits.map((benefit) => ({
          ...benefit,
          label: benefitLabels[benefit.id] ?? benefit.label,
        }));
    return (
      <FeatureCard
        key={feature.id}
        image={feature.image}
        name={feature.name}
        statusText={view.statusText}
        statusPlaceholders={view.statusPlaceholders}
        tone={view.tone}
        bar={{ value: feature.bar.value, market: feature.bar.market }}
        tierLabel={view.tierLabel}
        unlockText={view.unlockText}
        unlockPlaceholders={view.unlockPlaceholders}
        benefits={benefits}
        hideBenefits={newtonShowcase}
        ctaLabel={view.ctaLabel}
        // A pending payment gets a filled (higher-emphasis) CTA + an attention dot on the
        // artwork, mirroring the Product sidebar dot so the owner links the two.
        ctaVariant={hasPendingInvoice ? 'primary' : 'outline'}
        alert={hasPendingInvoice}
        onCta={() => onFeatureCta(feature.id)}
        animate={animate}
        delay={index * ANIM.STAGGER_MS}
      />
    );
  };

  return (
    <PageShell styleText={styleText} className={className}>
      {newtonShowcase ? null : (
        <InsightsPanel summary={summary} metrics={metrics} animate={insightsRevealed} loading={insightsLoading} />
      )}

      {calibrating && !insightsLoading && !newtonShowcase ? (
        <CalibrationNote>
          <CalibrationNoteIcon>
            <Icon name="info" size={14} color={COLORS.textMuted} />
          </CalibrationNoteIcon>
          <CalibrationNoteText tag="span" text="Calibrating market competitiveness and scores" />
        </CalibrationNote>
      ) : null}

      {missingFeatures.length > 0 ? (
        <Section>
          <SectionTitle title={t('Missing Features')} count="{{count}}" countPlaceholders={{ count: missingFeatures.length }} />
          <Container>
            <FeatureList>{missingFeatures.slice(0, MISSING_VISIBLE_COUNT).map(renderCard)}</FeatureList>
            {missingFeatures.length > MISSING_VISIBLE_COUNT ? (
              <Drawer $open={missingExpanded} aria-hidden={!missingExpanded}>
                <DrawerInner>
                  <DrawerList>
                    {missingFeatures
                      .slice(MISSING_VISIBLE_COUNT)
                      .map((feature, index) => renderCard(feature, index + MISSING_VISIBLE_COUNT))}
                  </DrawerList>
                </DrawerInner>
              </Drawer>
            ) : null}
            {missingFeatures.length > MISSING_VISIBLE_COUNT ? (
              <SeeMoreToggle
                label={missingExpanded ? t('See less features') : t('See more features')}
                icon={missingExpanded ? 'minus' : 'plus'}
                onClick={onToggleMissing}
              />
            ) : null}
          </Container>
        </Section>
      ) : null}

      <Section>
        <SectionTitle
          title={t('Enabled Features')}
          count={t('{{enabled}} of {{total}}')}
          countPlaceholders={{ enabled: enabledFeatures.length, total: totalCatalogCount }}
        />
        <Container>
          {enabledFeatures.length === 0 ? (
            <EmptyState text={t('No enabled features yet.')} />
          ) : (
            <FeatureList>{enabledFeatures.map(renderCard)}</FeatureList>
          )}
        </Container>
      </Section>
    </PageShell>
  );
};

export default Product;